/**
 * Logo checks for the partner form. Keep in sync with
 * server/helpers/partnerBrand.js.
 */

export const MIN_PNG_WIDTH = 576
export const MIN_PNG_HEIGHT = 216
export const MIN_LOGO_RATIO = 2
export const MAX_LOGO_RATIO = 3.5
export const MAX_LOGO_BYTES = 2 * 1024 * 1024

const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10]
const UNSAFE_SVG =
  /<script\b|<\/script|javascript\s*:|<\s*foreignObject\b|<\s*iframe\b|<\s*embed\b|<\s*object\b|\son[a-z]+\s*=/i

export function ratioOk(width, height) {
  if (!width || !height) return false
  const ratio = width / height
  return ratio >= MIN_LOGO_RATIO && ratio <= MAX_LOGO_RATIO
}

function readU32(bytes, offset) {
  return (
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0
  )
}

function isPng(bytes) {
  if (bytes.length < PNG_SIGNATURE.length) return false
  return PNG_SIGNATURE.every((value, index) => bytes[index] === value)
}

function hasChunk(bytes, name) {
  let offset = 8
  while (offset + 8 <= bytes.length) {
    const length = readU32(bytes, offset)
    const type = String.fromCharCode(
      bytes[offset + 4],
      bytes[offset + 5],
      bytes[offset + 6],
      bytes[offset + 7]
    )
    if (type === name) return true
    if (type === 'IEND') return false
    if (!Number.isFinite(length) || length > bytes.length) return false
    offset += 12 + length
  }
  return false
}

function inspectPng(bytes) {
  if (bytes.length < 29) return { ok: false, error: 'type' }
  const width = readU32(bytes, 16)
  const height = readU32(bytes, 20)
  if (width < MIN_PNG_WIDTH || height < MIN_PNG_HEIGHT) {
    return { ok: false, error: 'size' }
  }
  if (!ratioOk(width, height)) return { ok: false, error: 'ratio' }
  const colorType = bytes[25]
  const transparent =
    colorType === 4 || colorType === 6 || hasChunk(bytes, 'tRNS')
  if (!transparent) return { ok: false, error: 'transparent' }
  return { ok: true, ext: 'png', width, height }
}

function svgSize(text) {
  const viewBox = text.match(
    /viewBox\s*=\s*["']\s*([0-9.+-eE]+)[,\s]+([0-9.+-eE]+)[,\s]+([0-9.+-eE]+)[,\s]+([0-9.+-eE]+)\s*["']/i
  )
  if (viewBox) {
    const width = Number(viewBox[3])
    const height = Number(viewBox[4])
    if (width > 0 && height > 0) return { width, height }
  }
  const widthMatch = text.match(/\bwidth\s*=\s*["']\s*([0-9.]+)/i)
  const heightMatch = text.match(/\bheight\s*=\s*["']\s*([0-9.]+)/i)
  if (widthMatch && heightMatch) {
    const width = Number(widthMatch[1])
    const height = Number(heightMatch[1])
    if (width > 0 && height > 0) return { width, height }
  }
  return null
}

function inspectSvg(text) {
  if (/<!DOCTYPE html|<html[\s>]/i.test(text)) {
    return { ok: false, error: 'type' }
  }
  if (UNSAFE_SVG.test(text)) return { ok: false, error: 'unsafe' }
  const size = svgSize(text)
  if (size && !ratioOk(size.width, size.height)) {
    return { ok: false, error: 'ratio' }
  }
  return {
    ok: true,
    ext: 'svg',
    width: size?.width ?? null,
    height: size?.height ?? null,
  }
}

export function inspectPartnerLogo(input) {
  const bytes = input instanceof Uint8Array ? input : null
  if (!bytes || bytes.length < 8) return { ok: false, error: 'type' }
  if (bytes.length > MAX_LOGO_BYTES) return { ok: false, error: 'large' }
  if (isPng(bytes)) return inspectPng(bytes)
  const text = new TextDecoder('utf-8').decode(bytes).replace(/^\uFEFF/, '')
  if (/<svg[\s>]/i.test(text)) return inspectSvg(text)
  return { ok: false, error: 'type' }
}

export function whatsappHref(phone) {
  let digits = String(phone || '').replace(/\D/g, '')
  if (digits.startsWith('00')) digits = digits.slice(2)
  if (digits.startsWith('0')) digits = `972${digits.slice(1)}`
  if (digits.length < 8 || digits.length > 15) return ''
  return `https://wa.me/${digits}`
}

function apiOrigin() {
  const env = import.meta.env
  if (env?.DEV) return ''
  const base = env?.VITE_BASE_URL
  if (!base) return ''
  try {
    return new URL(base).origin
  } catch {
    return ''
  }
}

export function partnerLogoSrc(logoPath, origin = apiOrigin()) {
  if (!logoPath) return ''
  if (/^https?:\/\//i.test(logoPath)) return logoPath
  if (!origin) return logoPath
  return `${origin}${logoPath.startsWith('/') ? '' : '/'}${logoPath}`
}
